import { ALL_FACETS, RefinementFacet } from "../instant-search/refinements";
import {
  Box,
  Collapse,
  CollapseHeader,
  CollapsePanel,
  Text,
  useResponsiveValue,
} from "@vinomofo/components";
import { ClearRefiment, CurrentFilter } from "../refinement-menu/CurrentFilter";
import { useState } from "react";
import { SORT_OPTIONS, SortSelector } from "../sort-selector/SortSelector";
import styled, { css } from "styled-components";

import { CircleMinusIcon } from "@vinomofo/icons";
import { FilterOnlyList } from "./FilterOnlyList";
import { Media } from "gatsby-plugin-fresnel";
import { NumericMenu } from "./NumericMenu";
import { RefinementList } from "./RefinementList";
import config from "../../../config.json";
import { useInstantSearch } from "../../hooks/use-instantsearch";

const FilterGroupForFacet = ({
  facet: { facetType, label, attribute, urlKey, items, audience },
}: {
  facet: RefinementFacet;
}): JSX.Element => {
  if (!audience.includes(process.env.GATSBY_VM_MARKET)) {
    return null;
  }
  if (label && urlKey) {
    switch (facetType) {
      case "RefinementFacet":
        if (label === "Region" || label === "Varietal" || label === "Vintage")
          return (
            <RefinementList
              label={label}
              attribute={attribute}
              showMore={true}
              showMoreLimit={config.ALGOLIA_SHOW_MORE_LIMIT}
            />
          );

        return <RefinementList label={label} attribute={attribute} />;
      case "MultiRangeFacet":
        return (
          <NumericMenu label={label} attribute={attribute} items={items} />
        );
      case "FilterOnlyFacet":
        return <FilterOnlyList label={label} attribute={attribute} />;
      default:
        return null;
    }
  }
  return null;
};

const RefinementMenuWrap = styled(Box)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    width: 50%;
    flex-wrap: wrap;
  }
`;

const StyledCollapsePanel = styled(CollapsePanel)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    background: ${({ theme }) => theme.colors.white};
    margin-top: 0;
    border: 1px solid ${({ theme }) => theme.colors.divider};
  }
`;

const StyledCollapseTrigger = styled(CollapseHeader)`
  @media ${({ theme }) => theme.mq.max.tablet} {
    width: 100%;
  }
`;

const DropDownOverlayStyleOpen = () => css`
  margin: 0;
  padding: 0;
  z-index: 15;
  position: fixed;
  opacity: 1;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0);
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-align-items: flex-start;
  -webkit-box-align: flex-start;
  -ms-flex-align: flex-start;
  align-items: flex-start;
  -webkit-transition: opacity 200ms linear;
  transition: opacity 200ms linear;
`;

const DropDownOverlay = styled.div<{ open?: boolean }>`
  ${(props) =>
    props.open ? DropDownOverlayStyleOpen() : "opacity: 0; z-index: -1;"};
`;

export const RefinementMenu = ({
  totalHit,
}: {
  totalHit?: number;
}): JSX.Element => {
  const { searchState, hasResult } = useInstantSearch();
  const [isOpen, setIsOpen] = useState(false);
  const shouldOpen = useResponsiveValue([false, false, true]);

  const filterCount = (obj: Record<string, any>): number => {
    let totalArrayCount = 0;
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        totalArrayCount += obj[key].length;
      }
    }
    return hasResult ? totalArrayCount + 1 : totalArrayCount;
  };

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <>
      <Box
        mr={[0, 0, "40px"]}
        mb={["0", "0", "40px"]}
        borderTop={["0", "0", "1px solid"]}
        borderTopColor={["divider", "divider", "divider"]}
        position={["relative", "relative", "unset"]}
        display={["flex", "flex", "unset"]}
        flexWrap={["wrap", "wrap", "nowrap"]}
      >
        <SortSelector
          defaultRefinement={`${process.env.GATSBY_ALGOLIA_OFFERS_INDEX}`}
          items={SORT_OPTIONS}
          width={["50%", "50%", "100%"]}
        />
        <RefinementMenuWrap
          display={["flex", "flex", "flex"]}
          borderBottom={["1px solid", "1px solid", 0]}
          borderTop={["1px solid", "1px solid", "0"]}
          borderRight={["1px solid", "1px solid", 0]}
          borderColor={["divider", "divider", "divider"]}
          borderRadius={["6px 6px 0 0", "6px 6px 0 0", "0 0 6px 6px", 0]}
        >
          <StyledCollapseTrigger
            display={["flex", "flex", "none"]}
            p={[12, 15, 20]}
            rotation={45}
            iconSize="16px"
            closeIcon={<CircleMinusIcon />}
            onClick={toggleOpen}
            zIndex="15"
          >
            <Text fontSize={[1, 2, 2]} fontWeight="500" color="button">
              Filters: ({filterCount(searchState?.refinementList)})
            </Text>
          </StyledCollapseTrigger>
          <Box width="100%">
            <DropDownOverlay open={isOpen} onClick={toggleOpen} />
            <Box
              position={["absolute", "absolute", "relative"]}
              width="100%"
              left="0"
              zIndex="15"
            >
              <Collapse isOpen={isOpen || shouldOpen} onClick={toggleOpen}>
                <StyledCollapsePanel padding="0px">
                  <Box minWidth={["auto", "auto", 260]}>
                    {ALL_FACETS.map((facet, i) => (
                      <FilterGroupForFacet
                        key={`${facet.attribute}-${i}`}
                        facet={facet}
                      />
                    ))}

                    <Media between={["sm", "lg"]}>
                      <ClearRefiment />
                    </Media>
                  </Box>
                </StyledCollapsePanel>
              </Collapse>
            </Box>
          </Box>
        </RefinementMenuWrap>
        <Box
          display={["flex", "flex", "none"]}
          justifyContent="space-between"
          alignItems="center"
          p={[12, 15, 20]}
          borderBottom={["1px solid", "1px solid", "1px solid"]}
          borderTop={["1px solid", "1px solid", "0"]}
          borderRight={["1px solid", "1px solid", "0"]}
          borderLeft={["1px solid", "1px solid", "0"]}
          borderColor={["divider", "divider", "divider"]}
          borderRadius={0}
          width="100%"
          mt="-1px"
        >
          <Text fontSize={[1]} fontWeight="500" color="darkMofo" as="small">
            Results found:
          </Text>
          <Text fontSize={[1]} fontWeight="500" color="darkMofo" as="small">
            ({totalHit})
          </Text>
        </Box>
      </Box>
      <Media between={["sm", "lg"]}>
        <br />
        <CurrentFilter totalHit={totalHit || 0} />
        <br />
        {searchState?.refinementList &&
          Object.keys(searchState?.refinementList).length > 0 && (
            <>
              <br />
            </>
          )}
      </Media>
    </>
  );
};
